import React from "react";
import { IconButton, SxProps, Theme } from "@mui/material";
import { useSharedColors } from "../../theme/SharedComponentsTheme";

export interface CustomCircleIconButtonProps {
  /** アイコンだけのボタンなので、読み上げ用の名前を必ず渡す。 */
  ariaLabel: string;
  /** 表示するアイコン。 */
  children: React.ReactNode;
  onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  /** 円の直径(px)。 */
  size?: number;
  disabled?: boolean;
  sx?: SxProps<Theme>;
  "data-testid"?: string;
}

/**
 * 円形のアイコンボタン（戻る「‹」、閉じるなど）。
 * 通常は枠線・背景なし、ホバー・キーボードフォーカスで青の円の枠線と薄い青の背景、押下で軽く縮む。
 */
const CustomCircleIconButton: React.FC<CustomCircleIconButtonProps> = ({
  ariaLabel,
  children,
  onClick,
  size = 32,
  disabled = false,
  sx,
  "data-testid": dataTestId,
}) => {
  const colors = useSharedColors();
  const activeStyle = {
    backgroundColor: colors.background.selected,
    borderColor: colors.primary.main,
    color: colors.primary.main,
  };

  return (
    <IconButton
      aria-label={ariaLabel}
      onClick={onClick}
      disabled={disabled}
      disableRipple
      data-testid={dataTestId}
      sx={[
        {
          width: size,
          height: size,
          p: 0,
          flexShrink: 0,
          boxSizing: "border-box",
          borderRadius: "50%",
          // 通常時も透明の枠線を持たせ、ホバーで枠線が出ても円の大きさを変えない。
          border: "1px solid transparent",
          color: colors.text.secondary,
          transition:
            "background-color 0.15s ease, border-color 0.15s ease, color 0.15s ease, transform 0.1s ease",
          "&:hover, &.Mui-focusVisible": activeStyle,
          "&:active": { ...activeStyle, transform: "scale(0.92)" },
          "&.Mui-disabled": { color: colors.text.disabled },
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      {children}
    </IconButton>
  );
};

export default CustomCircleIconButton;
